/**
 * Payment-Link React-Query Hooks (Spec 493 Inc 1 read + Spec 497 Inc 2 mutations).
 *
 * Lese-Queries: das Services-Aggregat (Uebersicht) und der Merchant-Status (Drawer).
 * Mutationen (Inc 2): aktivieren/deaktivieren + Salt rotieren. Die Mutationen invalidieren
 * die Status-Queries; der SaltCleartext wird aus dem Mutations-Response gelesen, NICHT aus
 * der Invalidierung/einem Refetch (D-Reveal) — der Aufrufer liest ihn aus `data`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPaymentLinkMerchant,
  getPaymentLinkServiceConfig,
  listPaymentLinkServices,
  removePaymentLinkServiceConfig,
  rotatePaymentLinkSalt,
  setPaymentLinkMerchantEnabled,
  setPaymentLinkServiceConfig,
} from '@/lib/api/payment-link';
import { ApiError } from '@/lib/api/client';
import type { PaymentLinkServiceConfigRequest } from '@/types/payment-link';

export const paymentLinkKeys = {
  all: ['payment-link'] as const,
  services: () => [...paymentLinkKeys.all, 'services'] as const,
  merchant: (merchantId: string) => [...paymentLinkKeys.all, 'merchant', merchantId] as const,
  serviceConfig: (merchantId: string, serviceId: string) =>
    [...paymentLinkKeys.all, 'service-config', merchantId, serviceId] as const,
};

/**
 * Retry-Policy für die Wizard-Queries (D-Query404): die erwarteten 404 (`service_not_configured`
 * / `provisioning_record_not_found`) sind terminale Wizard-Zustände, KEIN transienter Fehler —
 * ein `ApiError` (jedes 4xx ist deterministisch) darf nicht 3× retryt werden, bevor die UI
 * verzweigt. Nur echte Transient-/Netzwerkfehler bekommen einen begrenzten Retry.
 */
// Der `error`-Parameter MUSS `Error` (nicht `unknown`) sein: TanStack Query leitet `TError` aus
// der `retry`-Callback-Signatur ab — mit `unknown` kippt `TError` an JEDER Nutzungsstelle des Hooks
// auf `unknown` (auch in unveraenderten Konsumenten wie dem Merchant-Drawer, der `error` an
// `ApiErrorDisplay` weiterreicht → tsc-Bruch). `Error` haelt die Default-Inferenz; `ApiError extends
// Error`, das `instanceof` bleibt gueltig.
function retryNon4xx(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/** Services-Aggregat (Basis der Merchant-Uebersicht). */
export function usePaymentLinkServices() {
  return useQuery({
    queryKey: paymentLinkKeys.services(),
    queryFn: () => listPaymentLinkServices(),
    staleTime: 30_000,
  });
}

/** Merchant-Provisionierungs-Status (Drawer + Wizard). Nur aktiv, wenn ein Merchant gewaehlt ist. */
export function usePaymentLinkMerchant(merchantId: string | undefined) {
  return useQuery({
    queryKey: paymentLinkKeys.merchant(merchantId ?? ''),
    queryFn: () => getPaymentLinkMerchant(merchantId!),
    enabled: !!merchantId,
    staleTime: 30_000,
    // D-Query404: ein 404 (nicht provisioniert) ist ein Wizard-Zustand, kein retrybarer Fehler.
    retry: retryNon4xx,
  });
}

/**
 * Service-Config eines Service (Wizard/Config-Ansicht). `404 service_not_configured` ist der
 * „noch nicht konfiguriert"-Normalzustand (D-NotConfigured) — der Aufrufer liest den gesettelten
 * `ApiError` (nicht ein Hook-Sonderfeld). Kein Retry auf 4xx (D-Query404).
 */
export function usePaymentLinkServiceConfig(
  merchantId: string | undefined,
  serviceId: string | undefined,
) {
  return useQuery({
    queryKey: paymentLinkKeys.serviceConfig(merchantId ?? '', serviceId ?? ''),
    queryFn: () => getPaymentLinkServiceConfig(merchantId!, serviceId!),
    enabled: !!merchantId && !!serviceId,
    staleTime: 30_000,
    retry: retryNon4xx,
  });
}

/** Invalidiert Uebersicht + Merchant-Status nach einer Mutation (Statusanzeige aktualisieren). */
function invalidatePaymentLink(
  queryClient: ReturnType<typeof useQueryClient>,
  merchantId: string,
): void {
  queryClient.invalidateQueries({ queryKey: paymentLinkKeys.services() });
  queryClient.invalidateQueries({ queryKey: paymentLinkKeys.merchant(merchantId) });
}

/**
 * Merchant aktivieren/deaktivieren (Inc 2). Gibt die Mutations-Antwort zurueck — der Aufrufer
 * liest `SaltCleartext` daraus (Reveal), NICHT aus der Invalidierung. Ein transient
 * fehlgeschlagenes Refetch darf den bereits erfolgten Reveal nicht verhindern.
 */
export function useSetPaymentLinkMerchantEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, enabled }: { merchantId: string; enabled: boolean }) =>
      setPaymentLinkMerchantEnabled(merchantId, enabled),
    onSuccess: (_data, { merchantId }) => invalidatePaymentLink(queryClient, merchantId),
  });
}

/** Salt rotieren (Inc 2). Antwort traegt den neuen `SaltCleartext` (Reveal-Quelle). */
export function useRotatePaymentLinkSalt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId }: { merchantId: string }) => rotatePaymentLinkSalt(merchantId),
    onSuccess: (_data, { merchantId }) => invalidatePaymentLink(queryClient, merchantId),
  });
}

/**
 * Service freischalten/konfigurieren (Inc 2b/3). Die Antwort traegt bei Webhook-Erst-Aktivierung
 * einmalig `ClickWebhookSecretCleartext` — der Aufrufer liest ihn aus `data` (Reveal-Quelle),
 * NICHT aus der Invalidierung/einem Refetch. Invalidiert Uebersicht + Merchant + Service-Config.
 */
export function useSetPaymentLinkServiceConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      merchantId,
      serviceId,
      body,
    }: {
      merchantId: string;
      serviceId: string;
      body: PaymentLinkServiceConfigRequest;
    }) => setPaymentLinkServiceConfig(merchantId, serviceId, body),
    onSuccess: (_data, { merchantId, serviceId }) => {
      invalidatePaymentLink(queryClient, merchantId);
      queryClient.invalidateQueries({
        queryKey: paymentLinkKeys.serviceConfig(merchantId, serviceId),
      });
    },
  });
}

/** Service-Freischaltung entfernen (Inc 2b/3). Invalidiert Uebersicht + Merchant + Service-Config. */
export function useRemovePaymentLinkServiceConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, serviceId }: { merchantId: string; serviceId: string }) =>
      removePaymentLinkServiceConfig(merchantId, serviceId),
    onSuccess: (_data, { merchantId, serviceId }) => {
      invalidatePaymentLink(queryClient, merchantId);
      queryClient.invalidateQueries({
        queryKey: paymentLinkKeys.serviceConfig(merchantId, serviceId),
      });
    },
  });
}
